import { ClampBounds } from "../action/Clamp.js";
import { Caps } from "../core/Caps.js";
import { Calc } from "../math/Calc.js";
import { IPoint, Point } from "./Point.js";
import { Quad } from "./Quad.js";

export interface IRect extends IPoint {
    width: number;
    height: number;
}

export class Rect implements IRect {
    constructor(
        public x: number = 0,
        public y: number = 0,
        public width: number = 0,
        public height: number = 0) {
    }

    public clone(): Rect {
        return new Rect(
            this.x,
            this.y,
            this.width,
            this.height);
    }

    public setTo(x: number, y: number, width: number, height: number): Rect {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;

        return this;
    }

    public toQuad(ret: Quad = new Quad()): Quad {
        ret.p1.x = this.x;
        ret.p1.y = this.y;
        ret.p2.x = this.right;
        ret.p2.y = this.y;
        ret.p3.x = this.right;
        ret.p3.y = this.bottom;
        ret.p4.x = this.x;
        ret.p4.y = this.bottom;
        return ret;
    }

    public toPoints(): IPoint[] {
        return [
            //Top-Left
            {
                x: this.x,
                y: this.y
            },
            //Top-Right
            {
                x: this.x + this.width,
                y: this.y
            },
            //Bottom-Right
            {
                x: this.x + this.width,
                y: this.y + this.height
            },
            //Bottom-Left
            {
                x: this.x,
                y: this.y + this.height
            }
        ];
    }

    /**
     * @deprecated Not in use anymore.
     */
    public setCSS(clipCSS: string): Rect {
        if (!clipCSS || clipCSS == "auto"){
            return this;
        }
        //TODO: using a regex -> performance testing
        var str = clipCSS.toLowerCase().replace("rect(", "").replace(")", "");
        var values = Caps.isWebkit ? str.split(" ") : str.split(",");

        this.x = Calc.parse(values[3], 0);
        this.y = Calc.parse(values[0], 0);
        this.width = Calc.parse(values[1], 0) - this.x;
        this.height = Calc.parse(values[2], 0) - this.y;

        return this;
    }

    /**
     * @deprecated Not in use anymore.
     */
    public getCSS(): string {
        return "rect("
            + Math.round(this.y) + "px, "
            + Math.round(this.right) + "px, "
            + Math.round(this.bottom) + "px, "
            + Math.round(this.x) + "px)";
    }

    public copyFrom(value: IRect): void {
        this.x = value.x;
        this.y = value.y;
        this.width = value.width;
        this.height = value.height;
    }

    public center(ret: Point = new Point()): Point {
        return ret.setTo(this.x + this.width * 0.5, this.y + this.width * 0.5);
    }

    public equals(value: IRect, difference: number = 0): boolean {
        return Math.abs(this.x - value.x) <= difference
            && Math.abs(this.y - value.y) <= difference
            && Math.abs(this.width - value.width) <= difference
            && Math.abs(this.height - value.height) <= difference;
    }

    public contains(x: number, y: number): boolean {
        return x >= this.x
            && x <= (this.x + this.width)
            && y >= this.y
            && y <= (this.y + this.height);
    }

    public containsRect(r: IRect): boolean {
        return r.x >= this.x
            && r.x <= this.x + this.width
            && r.y >= this.y
            && r.y <= this.y + this.height;
    }

    public intersects(r: IRect): boolean {
        if (this.contains(r.x, r.y)
            || this.contains(r.x + r.width, r.y + r.height)
            || this.contains(r.x + r.width, r.y)
            || this.contains(r.x, r.y + r.height)
        )
            return true;
        return false;
    }

    /**
     * 
     * @deprecated Use Rect.copyFrom() instead.
     */
    public copyFromClientRect(rect: DOMRect): Rect {
        this.copyFrom(rect);
        return this;
    }

    public get right(): number {
        return this.x + this.width;
    }

    public set right(value: number) {
        this.width = value - this.x;
    }

    public get bottom(): number {
        return this.y + this.height;
    }

    public set bottom(value: number) {
        this.height = value - this.y;
    }

    public setPoints(points: IPoint[]): void {
        var minX: number = Number.MAX_VALUE;
        var minY: number = Number.MAX_VALUE;
        var maxX: number = -Number.MAX_VALUE;
        var maxY: number = -Number.MAX_VALUE;
        for (var pt of points) {
            minX = Math.min(minX, pt.x);
            minY = Math.min(minY, pt.y);
            maxX = Math.max(maxX, pt.x);
            maxY = Math.max(maxY, pt.y);
        }

        this.x = minX;
        this.y = minY;
        this.width = maxX - minX;
        this.height = maxY - minY;
    }

    public static fromPoints(points: IPoint[]): Rect {
        var r = new Rect();
        r.setPoints(points);
        return r;
    }

    public static getBounds(element: HTMLElement, ret: Rect = new Rect()): Rect {
        ret.copyFrom(element.getBoundingClientRect());
        return ret;
    }

    public static getClip(element: HTMLElement, style: CSSStyleDeclaration, ret: Rect = new Rect()): Rect {
        if (!style.clip || style.clip == "auto") {
            ret.x = 0;
            ret.y = 0;
            ret.width = element.offsetWidth;
            ret.height = element.offsetHeight;
            return ret;
        }
        ret.setCSS(style.clip);
        return ret;
    }

    public static fromClampBounds(bounds: ClampBounds, containerWidth: number, containerHeight: number, rect: Rect = null): Rect {

        if (!rect) {
            rect = new Rect();
        }

        if (!bounds
            || bounds === "auto"
            || bounds === undefined) {
            rect.x = 0;
            rect.y = 0;
            rect.width = containerWidth;
            rect.height = containerHeight;
        }
        else if (typeof bounds === "string") {
            var valueX = Calc.relationCalced(bounds, containerWidth, 0);
            var valueY = Calc.relationCalced(bounds, containerHeight, 0);
            rect.x = valueX;
            rect.y = valueY;
            rect.right = containerWidth - valueX;
            rect.bottom = containerHeight - valueY;
        }
        else if (typeof bounds === "number") {
            rect.x = bounds;
            rect.y = bounds;
            rect.right = containerWidth - bounds;
            rect.bottom = containerHeight - bounds;
        }
        else {
            rect.x = Calc.relationCalced(bounds.left, containerWidth, 0);
            rect.y = Calc.relationCalced(bounds.top, containerHeight, 0);
            rect.right = containerWidth - Calc.relationCalced(bounds.right, containerWidth, 0);
            rect.bottom = containerHeight - Calc.relationCalced(bounds.bottom, containerHeight, 0);
        }

        return rect;
    }

    public dispose(): void {
    }

    public toString(): string {
        return "[Rect2D"
            + " x=" + this.x
            + " y=" + this.y
            + " width=" + this.width
            + " height=" + this.height
            + "]";
    }
}